import React from "react";
import { Breadcrumb } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./PageBreadcrumb.css";

/**
 * PageBreadcrumb — standardized breadcrumb + back button for all pages.
 *
 * @param {Array}    items       Array of { label, path?, icon? }.
 *                               The last item is the current page (no path needed).
 * @param {Function} [onBack]    Custom back handler. Defaults to navigate(-1).
 * @param {string}   [className] Extra CSS class on the wrapper.
 *
 * Usage:
 *   <PageBreadcrumb
 *     items={[
 *       { label: 'Users',            path: '/users' },
 *       { label: 'Business Profile', path: `/users/profile/${id}/page` },
 *       { label: 'Transaction History' },
 *     ]}
 *   />
 */
const PageBreadcrumb = ({ items = [], onBack, className = "" }) => {
    const navigate = useNavigate();

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    const breadcrumbItems = items.map((item, index) => {
        const isLast = index === items.length - 1;
        return {
            title: isLast ? (
                <span className="page-crumb-current">
                    {item.icon && <span style={{ marginRight: 4 }}>{item.icon}</span>}
                    {item.label}
                </span>
            ) : (
                <span
                    className="page-crumb-link"
                    onClick={() => item.path && navigate(item.path)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && item.path && navigate(item.path)}
                >
                    {item.icon && <span>{item.icon}</span>}
                    {item.label}
                </span>
            ),
        };
    });

    return (
        <div className={`page-breadcrumb-bar ${className}`.trim()}>
            <button className="page-back-btn" onClick={handleBack} type="button">
                <ArrowLeftOutlined />
                Back
            </button>
            <Breadcrumb className="page-breadcrumb" items={breadcrumbItems} />
        </div>
    );
};

export default PageBreadcrumb;
